import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* retry */ }
      if (tries >= 8) { setStatus("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <div className="mb-8"><Logo /></div>
      {status === "checking" && (
        <>
          <Loader2 size={40} className="animate-spin text-[#09090b]" />
          <h1 className="font-heading font-bold text-2xl text-[#09090b] mt-6">Confirming your payment…</h1>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={56} className="text-emerald-500" />
          <h1 className="font-heading font-bold text-3xl text-[#09090b] mt-6">You're upgraded! 🎉</h1>
          <p className="font-body text-[#52525b] mt-2">Your plan is now active. Time to build.</p>
          <Link to="/dashboard" data-testid="go-dashboard" className="mt-8 ml-ai-btn rounded-full px-7 py-3 font-semibold text-sm font-body">Go to Dashboard</Link>
        </>
      )}
      {status === "pending" && (
        <>
          <Loader2 size={40} className="text-amber-500" />
          <h1 className="font-heading font-bold text-2xl text-[#09090b] mt-6">Payment processing</h1>
          <p className="font-body text-[#52525b] mt-2">This is taking a moment. Check your dashboard shortly.</p>
          <Link to="/dashboard" className="mt-8 bg-[#09090b] text-white rounded-full px-7 py-3 font-semibold text-sm font-body">Go to Dashboard</Link>
        </>
      )}
      {status === "error" && (
        <>
          <XCircle size={56} className="text-red-500" />
          <h1 className="font-heading font-bold text-2xl text-[#09090b] mt-6">Something went wrong</h1>
          <Link to="/pricing" className="mt-8 bg-[#09090b] text-white rounded-full px-7 py-3 font-semibold text-sm font-body">Back to Pricing</Link>
        </>
      )}
    </div>
  );
}
